/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Tooltip } from '@blend/enterprise-components';

import { useDispatch } from 'react-redux';
import { notaryAPI } from '../../../api/notary';
import { hasCallStarted, startCall } from '../../../utils/call';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { ConfirmationModal } from '../Modals/ConfirmationModal/ConfirmationModal';
import SmallToggle from '@components/form/Inputs/SmallToggle/SmallToggle';
import { getConfig } from '@utils/configs';

const styles = StyleSheet.create({
  toggle: {},
});

export namespace StartToggle {
  export interface OwnProps {
    callId: string;
  }
  export type Props = OwnProps;
}

export const StartToggle: React.FC<StartToggle.Props> = ({ callId, ...props }) => {
  const [isChecked, setIsChecked] = useState(false);
  const dispatch = useDispatch();
  const [isDisabled, setIsDisabled] = useState(false);
  const [allowSigning, setAllowSigning] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const users = useRootSelector(state => state.signingRoom.users);
  const usersInVideoCall = users.inVideoCall;
  const callDetails = useRootSelector(state => state.callDetails);
  const isManualVerificaionByNotaryEnabled = getConfig().isManualVerificaionByNotaryEnabled;

  function checkParticipantApprovals() {
    const nonNotaryUsers = usersInVideoCall.filter(session => session.isNotary !== true).map(user => user.users);
    const areAllusersApproved = nonNotaryUsers.flat().every(user => !user.showApprovalButton);
    return areAllusersApproved;
  }

  useEffect(() => {
    const refreshSigningStatus = async () => {
      const { status } = await notaryAPI.calls.fetchCall(callId);
      setIsChecked(status === 'SIGNING');
    };
    refreshSigningStatus();
  }, [callId]);

  useEffect(() => {
    const areAllusersApproved = checkParticipantApprovals();
    setAllowSigning(areAllusersApproved);
  }, [usersInVideoCall]);

  // Watch callDetails.status from Redux to keep toggle in sync
  useEffect(() => {
    if (callDetails?.status !== undefined) {
      setIsChecked(callDetails.status === 'SIGNING');
    }
  }, [callDetails?.status]);

  async function toggleSigning() {
    if (isChecked) {
      await notaryAPI.calls.pauseSigning(callId);
      setIsChecked(false);
      return;
    }

    if (isManualVerificaionByNotaryEnabled) {
      if (allowSigning) {
        startSigning();
      } else {
        setOpenModal(true);
      }
    } else {
      startSigning();
    }
  }

  async function startSigning() {
    if (!(await hasCallStarted(callId))) {
      setIsDisabled(true);
      await startCall(callId, dispatch);
      setIsDisabled(false);
    }

    await notaryAPI.calls.startSigning(callId);
    setIsChecked(true);
  }

  const modalDescription =
    'Manual ID verification must be completed for all borrowers before document signing can start. ID verification can be completed by verifying the photo ID of participants in the Participants tab.';

  const SigningToggle = () => (
    <SmallToggle
      {...props}
      label={'Document Signing'}
      data-it="DocumentSigningToggle"
      onChange={toggleSigning}
      checked={isChecked}
      disabled={isDisabled}
    />
  );

  return (
    <div className={css(styles.toggle)}>
      {isDisabled ? (
        <Tooltip
          position="bottom"
          text={'Please wait while we are preparing your documents for signing.'}
          theme="transparent"
        >
          <SigningToggle />
        </Tooltip>
      ) : (
        <SigningToggle />
      )}

      {openModal && (
        <ConfirmationModal
          heading={'Identity verification incomplete'}
          contentLabel={'Extend Session Modal'}
          isOpen={openModal}
          description={modalDescription}
          confirmButtonText={'Okay'}
          handleConfirm={() => setOpenModal(false)}
          data-it={'id-verification-incomplete-modal'}
        />
      )}
    </div>
  );
};
